import { z } from 'zod';

import { apiEnvelope } from '../envelope';

// ── staff API ───────────────────────────────────────────────────────────────

/**
 * What care staff are told about a member's tobacco surcharge. `yes` is a
 * client that applies a surcharge and a member in the tobacco programme;
 * `not_provided` is the client's config not saying; `unknown` is Galapagos not
 * being able to find out (no sponsor, an unsupported sponsor ref, or
 * internaltools failing). The two are kept apart on purpose.
 */
export const TOBACCO_SURCHARGE_STATUSES = ['yes', 'no', 'not_provided', 'unknown'] as const;
export type TobaccoSurchargeStatus = (typeof TOBACCO_SURCHARGE_STATUSES)[number];

export const memberTobaccoSurchargeDataSchema = z.object({
  status: z.enum(TOBACCO_SURCHARGE_STATUSES),
  asOf: z.string().nullable(),
});
export type MemberTobaccoSurchargeData = z.infer<typeof memberTobaccoSurchargeDataSchema>;

export const memberTobaccoSurchargeResponseSchema = apiEnvelope(memberTobaccoSurchargeDataSchema);
export type MemberTobaccoSurchargeResponse = z.infer<typeof memberTobaccoSurchargeResponseSchema>;

/**
 * The Atlas parse of the same body. A status this bundle does not know reads as
 * `unknown` instead of throwing (ADR-135 Rule 3): a bundle built today must keep
 * rendering when the server learns a new answer.
 */
export const memberTobaccoSurchargeClientDataSchema = z.object({
  status: z
    .string()
    .transform(
      (value): TobaccoSurchargeStatus =>
        TOBACCO_SURCHARGE_STATUSES.find((known) => known === value) ?? 'unknown',
    ),
  asOf: z.string().nullable(),
});
export type MemberTobaccoSurchargeClientData = z.infer<
  typeof memberTobaccoSurchargeClientDataSchema
>;

export const memberTobaccoSurchargeClientResponseSchema = apiEnvelope(
  memberTobaccoSurchargeClientDataSchema,
);

// ── member self read (the app) ──────────────────────────────────────────────

/**
 * Whether surcharge-specific member features may be shown. Server-decided and
 * fail-closed: only a positive answer turns them on.
 * "The client did not say", "we could not find out" and every failure keep them
 * off, so a client never has to interpret the tri-state itself.
 */
export function deriveSurchargeFeaturesEnabled(status: TobaccoSurchargeStatus): boolean {
  return status === 'yes';
}

export const memberSelfTobaccoSurchargeDataSchema = z.object({
  tobaccoSurcharge: z.enum(TOBACCO_SURCHARGE_STATUSES),
  surchargeFeaturesEnabled: z.boolean(),
});
export type MemberSelfTobaccoSurchargeData = z.infer<typeof memberSelfTobaccoSurchargeDataSchema>;

export const memberSelfTobaccoSurchargeResponseSchema = apiEnvelope(
  memberSelfTobaccoSurchargeDataSchema,
);

/**
 * The app's parse of the same body: a tri-state value this bundle does not know
 * reads as `unknown`, and the server's decision is kept as sent (ADR-135).
 */
export const memberSelfTobaccoSurchargeClientResponseSchema = apiEnvelope(
  z.object({
    tobaccoSurcharge: z
      .string()
      .transform(
        (value): TobaccoSurchargeStatus =>
          TOBACCO_SURCHARGE_STATUSES.find((known) => known === value) ?? 'unknown',
      ),
    surchargeFeaturesEnabled: z.boolean(),
  }),
);
